import { type PropsWithChildren, useEffect, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useLingui } from '@lingui/react'
import { Elements } from '@stripe/react-stripe-js'
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'

import { useFeatures, useGetConfig } from '@jotta/grpc-connect-client/config'

const debug = Debug('jotta:settings:StripeContainer')

let stripePromise: Promise<Stripe | null> | null = null

/*Opt-out of deep data collection from Stripe and stop cookies
loading for partners who do not use stripe. The Stripe cookies
load for brands that use stripe.
*/
loadStripe.setLoadParameters({ advancedFraudSignals: false })
async function loadStripeConfig(
  stripePublicKey: string,
): Promise<Stripe | null> {
  return loadStripe(stripePublicKey)
}

type Props = PropsWithChildren<{ publicKey?: string }>
export const StripeElementsContainer = observer<Props>(
  function StripeElementsContainer({ children, publicKey }) {
    const { i18n } = useLingui()
    const features = useFeatures()
    const brandingInfo = useGetConfig().data?.brandingInfo
    const stripePublicKey = publicKey || brandingInfo?.stripePublicKey

    const stripeLocale: StripeElementLocale =
      (i18n.locale as StripeElementLocale) || 'auto'

    const shouldLoadStripe =
      Boolean(stripePublicKey) &&
      (Boolean(publicKey) || (!features.isPending && features.stripe))

    const [stripe, setStripe] = useState<Promise<Stripe | null> | null>(null)

    useEffect(() => {
      if (!shouldLoadStripe || !stripePublicKey) {
        return
      }
      if (!stripePromise) {
        stripePromise = loadStripeConfig(stripePublicKey)
      }
      setStripe(stripePromise)
    }, [shouldLoadStripe, stripePublicKey])

    return (
      <Elements
        options={{
          locale: stripeLocale,
        }}
        stripe={stripe}
      >
        {children}
      </Elements>
    )
  },
)
